import React, { useContext, useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Alert, AlertTitle, Box, Button, Container, LinearProgress, Paper, Typography } from "@mui/material";
import bkstApi from "../../common/api";
import ConfigContext from "../../common/context/ConfigContext";
import { useNavigate } from "react-router";
import RequestSummary from "../component/RequestSummary";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import { convertOptions, isAssistedCheckout } from "../../common/util";
import moment from "moment";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LineItem from "../../common/component/LineItem";
import QuoteChat from "../component/QuoteChat";
import BlockingError from "../../common/component/BlockingError";
import ProductVariants from "../../product/component/ProductVariants";

export default function QuoteV4() {
  const siteConfig = useContext(ConfigContext);
  const navigate = useNavigate();

  const ref = useRef(null);

  const [request, setRequest] = useState("");
  const [quote, setQuote] = useState("");

  const [items, setItems] = useState("");

  const { reqId } = useParams();

  const [loading, setLoading] = useState(true);
  const [blockingError, setBlockingError] = useState("");

  const staffAssist = isAssistedCheckout();

  useEffect(() => {
    bkstApi(`/site/${siteConfig.id}/quote/${reqId}`).then((res) => {
      document.title = res.data.request.title;
      setRequest(res.data.request);
      setQuote(res.data.quote);

      if (res.data?.quote?.items) {
        const arr = res.data.quote.items;
        for (let o of arr) {
          if (o.variant?.flavor && Array.isArray(o.variant.flavor)) {
            const oldFmtFlavor = o.variant.flavor[0];
            if (oldFmtFlavor.name) {
              o.variant.flavor = { name: oldFmtFlavor.name };
            }
            if (oldFmtFlavor.filling) {
              o.variant.filling = { name: oldFmtFlavor.filling };
            }
          }
        }
        setItems(res.data.quote.items);
      }
      setLoading(false);

      ref.current.scrollIntoView();
    });
  }, [reqId]);

  const addToCart = (e) => {
    e.preventDefault();

    let payload = { type: "quote", refId: request.id, items, fmt: "2.0", updTs: quote.updTs };

    bkstApi
      .post(`/site/${siteConfig.id}/cart`, payload)
      .then((res) => {
        navigate(`/cart/${res.data.cartId}`);
      })
      .catch((error) => {
        setBlockingError(error.response.data.msg);
      });
  };

  if (loading) {
    return <LinearProgress />;
  } else {
    let quoteStatus = quote.status;
    let expiration = quote.exdate && quote.exdate.includes("T") ? quote.exdate : `${quote.exdate}T23:59`;
    if (quote.status === "active" && moment(expiration).diff(moment(), "minutes") < 0) {
      quoteStatus = "expired";
    } else if (quote.status === "block") {
      quoteStatus = "unavailable";
    }

    let chatButton = <QuoteChat reqId={request.id} baker={quote.baker} name={quote.baker.bizName} chat={quote.chat} />;

    if (request?.status === "new" || request?.status === "pending") {
      return (
        <Container maxWidth="sm" ref={ref}>
          <Box pt={10} pb={20}>
            <Box>
              <Alert severity="success">
                <AlertTitle>Thank you for requesting a quote!</AlertTitle>
                We'll review the details & send you a quote shortly. You'll receive a text message when your quote is ready. We look forward to
                serving you!
              </Alert>
            </Box>
            <Box my={4}>
              <RequestSummary request={request} />
            </Box>
            {chatButton}
          </Box>
        </Container>
      );
    } else if (quoteStatus === "unavailable") {
      return (
        <Container maxWidth="sm" ref={ref}>
          <Box pt={10} pb={20}>
            <Box textAlign="center">
              <SentimentVeryDissatisfiedIcon sx={{ fontSize: "5rem", color: "#e74c3c" }} />
              <Typography variant="h4" gutterBottom>
                We're Sorry!
              </Typography>
              <Typography variant="body1" color="textSecondary">
                We are unable to provide a quote at this time
              </Typography>
            </Box>
            <Box my={4}>
              <Alert severity="error">
                <AlertTitle>Reason</AlertTitle>
                {quote.note ||
                  `This is because we may have sent you another quote and this is a duplicate. Or, you may have informed us that you're no longer interested in a quote. If you believe this is an error, please message us below.`}
              </Alert>
            </Box>
            <Box my={4}>
              <RequestSummary request={request} />
            </Box>
            {chatButton}
          </Box>
        </Container>
      );
    } else if (quoteStatus === "active" || quoteStatus === "expired") {
      return (
        <Container maxWidth="sm" ref={ref}>
          <Box pt={10} pb={20}>
            <form onSubmit={addToCart}>
              <Box>
                <Box>
                  <Typography variant="h2" gutterBottom>
                    👋 {request.firstName || request.name || ""},
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    Here's your Custom Quote!
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Please review the details below & let us know if you have any questions. When you're ready to order, simply scroll down & tap the
                    Proceed to Checkout button.{" "}
                  </Typography>
                </Box>
                <Box my={4}>
                  <Typography variant="overline" color="textSecondary">
                    ITEMS
                  </Typography>
                  {items.map((item, idx) => {
                    let options = {};

                    if (item.options) {
                      if (item.options.flavors) {
                        options.flavor = convertOptions("Flavor", item.options.flavors, 8);
                      }
                      if (item.options.fillings) {
                        options.filling = convertOptions("Filling", item.options.fillings, 7);
                        if (
                          siteConfig.id === "IpDJo6abalT5OKbkFX6C" ||
                          siteConfig.id === "5yGl3okejphd9VyjiMQk" ||
                          siteConfig.id === "-MVIf1diaYOCm5KynVDF"
                        ) {
                          options.filling.required = false;
                        }
                      }
                      if (item.options.inscription) {
                        options.inscription = {
                          name: "Inscription",
                          description: `Add a greeting like "Happy Birthday", a name like "Alice" or a number like "4"`,
                          rank: 0,
                          field: {
                            type: "text",
                            min: 1,
                            max: 40
                          }
                        };
                      }
                      console.log("setting optons", options);
                    }

                    return (
                      <Box key={idx} my={2}>
                        <Paper elevation={0}>
                          <Box p={2}>
                            <LineItem item={item} />
                            {quoteStatus === "active" && Object.keys(options).length > 0 && (
                              <Box mt={2} p={2} sx={{ border: "1px solid #ECECEC" }}>
                                <Box mb={4}>
                                  <Typography variant="body1" gutterBottom>
                                    Select your Options
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    Tip: you can change your selection even after you place your order. Simply let us know a week before your order
                                    due date.
                                  </Typography>
                                </Box>
                                <ProductVariants
                                  basePrice={item.total}
                                  variant={item.variant}
                                  options={options}
                                  onChange={(key, value) => {
                                    let o = { ...item };
                                    if (!o.variant) {
                                      o.variant = {};
                                    }
                                    o.variant[key] = value;

                                    let arr = [...items];
                                    arr[idx] = o;
                                    setItems(arr);
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      </Box>
                    );
                  })}
                </Box>
                <Box my={2}>
                  {quoteStatus === "expired" ? (
                    <Alert severity="error">
                      Your quote has expired. If you're interested in booking, please message us using the chat widget to check availability..
                    </Alert>
                  ) : (
                    <Alert severity="info">
                      Quote expires{" "}
                      <u>
                        <b>{moment(expiration).fromNow()}</b>
                      </u>
                      . Price is subject to change after quote expires.
                    </Alert>
                  )}
                </Box>
              </Box>
              {quote.fulfillment.type === "delivery" && (
                <Box my={2}>
                  <Alert severity="success" icon={<LocalShippingOutlinedIcon />}>
                    Delivery is available! Choose the delivery option in checkout.
                  </Alert>
                </Box>
              )}
              {quoteStatus === "active" && (
                <Box my={4}>
                  <Button fullWidth variant="contained" color="primary" type="submit">
                    PROCEED TO CHECKOUT
                  </Button>
                </Box>
              )}
            </form>
            {!staffAssist && chatButton}
            {blockingError && (
              <BlockingError title="Error!" cta="Refresh Page" onClick={() => window.location.reload()}>
                <Box>
                  <Typography>{blockingError}</Typography>
                </Box>
              </BlockingError>
            )}
          </Box>
        </Container>
      );
    }
  }
}
